// packages/base/src/response-view/view-scope.ts
//
// View-scope filter — the *content / visibility* axis of a response-view,
// orthogonal to the *arrangement* axis owned by the LayoutIntent DSL
// (`layout-intent.ts`). Where LayoutIntent decides HOW a fixed node set is
// placed, ViewScope decides WHICH nodes are drawn: hide specific parts, or
// collapse a container to a faithful "black box".
//
// LOAD-BEARING INVARIANT (source-of-truth pillar): scoping NEVER edits the raw
// `view.sysml`. A scoped view is a faithful *partial* projection — the complete
// model always stays one action away in the SysML source. So this filter only
// ever DROPS/RE-ANCHORS nodes+edges of an already-materialised layout; it never
// invents anything.
//
// Pure `base` — no vscode/react/fastify/native imports. `applyViewScope()` is a
// total, pure transform that cannot fail, so (like `defaultLayoutIntent()`) it
// returns a plain value, not `Result<T,E>` — there is no cross-boundary failure
// mode to surface (CLAUDE.md).
/**
 * The empty (no-op) scope: nothing hidden, nothing collapsed. Returns a FRESH
 * object with fresh arrays on every call, so a caller that mutates one default's
 * arrays cannot poison the next caller's default.
 */
export function defaultViewScope() {
    return { hiddenNodeIds: [], collapsedContainers: [] };
}
/** True when the scope carries no directive at all (renders as the full view). */
export function isNoOpScope(scope) {
    return scope.hiddenNodeIds.length === 0 && scope.collapsedContainers.length === 0;
}
/**
 * Order-insensitive structural equality over two scopes. Suitable as the
 * "did the scope actually change?" comparator in the in-place scope loop
 * (mirrors `layoutIntentEquals`). Order-insensitive because the scope id lists
 * are sets in spirit — a re-ordering is not a meaningful change.
 */
export function viewScopeEquals(a, b) {
    const eqSet = (x, y) => {
        if (x.length !== y.length)
            return false;
        const sx = new Set(x);
        for (const v of y)
            if (!sx.has(v))
                return false;
        return true;
    };
    return (eqSet(a.hiddenNodeIds, b.hiddenNodeIds) &&
        eqSet(a.collapsedContainers, b.collapsedContainers));
}
/**
 * The strict-structured-output JSON schema for a `ViewScope`. Hand-written
 * (the shape is two string arrays) rather than derived from TypeBox so
 * `response-view/` needs no `@sinclair/typebox` import; a `view-scope.test.ts`
 * drift assertion pins it against `ViewScopeSchema`. `additionalProperties`
 * is `false` and both keys are `required`, matching the TypeBox SSOT (every
 * field is non-optional there) — suitable as the `json_schema` payload for the
 * LV-style "scope director" strict structured-output call.
 */
export const VIEW_SCOPE_JSON_SCHEMA = Object.freeze({
    type: 'object',
    additionalProperties: false,
    required: ['hiddenNodeIds', 'collapsedContainers'],
    properties: {
        hiddenNodeIds: { type: 'array', items: { type: 'string' } },
        collapsedContainers: { type: 'array', items: { type: 'string' } },
    },
});
/**
 * Collect the transitive descendants of `rootId` following `parentNode` edges.
 * `includeRoot` decides whether `rootId` itself joins the returned set —
 * `true` for a hard-hidden node (hide it AND its children), `false` for a
 * collapsed container (keep the box, hide only its children).
 */
function collectDescendants(rootId, childrenByParent, includeRoot, out) {
    if (includeRoot)
        out.add(rootId);
    const stack = [...(childrenByParent.get(rootId) ?? [])];
    while (stack.length > 0) {
        const id = stack.pop();
        if (out.has(id))
            continue;
        out.add(id);
        const kids = childrenByParent.get(id);
        if (kids !== undefined)
            stack.push(...kids);
    }
}
/**
 * Resolve the full set of node ids that a scope directive hides. This is the
 * union of:
 *   • every `hiddenNodeId` PLUS all of its `parentNode` descendants, and
 *   • all `parentNode` descendants of every `collapsedContainer` (the container
 *     id itself is NOT hidden — it stays as a black box).
 *
 * Exported for direct testing + reuse by a server-side scene projection.
 */
export function resolveHiddenNodeIds(layout, scope) {
    const hidden = new Set();
    if (isNoOpScope(scope))
        return hidden;
    const known = new Set(layout.nodes.map((n) => n.id));
    const childrenByParent = new Map();
    for (const raw of layout.nodes) {
        const parent = raw.parentNode;
        if (typeof parent === 'string' && parent.length > 0) {
            const arr = childrenByParent.get(parent) ?? [];
            arr.push(raw.id);
            childrenByParent.set(parent, arr);
        }
    }
    for (const id of scope.hiddenNodeIds) {
        if (known.has(id))
            collectDescendants(id, childrenByParent, true, hidden);
    }
    for (const id of scope.collapsedContainers) {
        if (known.has(id))
            collectDescendants(id, childrenByParent, false, hidden);
    }
    return hidden;
}
/**
 * Apply a layout's `viewScope` directive, returning a NEW layout whose node +
 * edge arrays are filtered to the visible set. Pure + total — never throws.
 *
 *  • Hidden nodes (see {@link resolveHiddenNodeIds}) are removed.
 *  • Each edge endpoint is re-anchored to its nearest still-visible ancestor
 *    (walk `parentNode` up until a visible node is found). An edge whose
 *    endpoint has NO visible ancestor is dropped; an edge that re-anchors to a
 *    self-loop (both ends resolve to the same visible node — i.e. it was
 *    internal to a collapsed container) is dropped. Re-anchored edges lose
 *    their port handles (the original ports live on now-hidden nodes) so they
 *    fall back to node-centre anchoring, and duplicate re-anchored edges are
 *    de-duplicated by `source|target` (keeping the first, preserving its id).
 *
 * When there is no `viewScope`, or it is a no-op, or nothing is actually hidden,
 * the input layout is returned UNCHANGED (same reference) — the switch-OFF /
 * legacy path is byte-identical.
 */
export function applyViewScope(layout) {
    const scope = layout.viewScope;
    if (scope === undefined || isNoOpScope(scope))
        return layout;
    const hidden = resolveHiddenNodeIds(layout, scope);
    if (hidden.size === 0)
        return layout;
    // parentNode chain lookup for re-anchoring.
    const parentOf = new Map();
    for (const raw of layout.nodes) {
        parentOf.set(raw.id, raw.parentNode);
    }
    /** Nearest visible ancestor of `id` (including `id`), or null if none. */
    const visibleAnchor = (id) => {
        let cursor = id;
        const guard = new Set(); // cycle guard (defensive)
        while (cursor !== undefined && !guard.has(cursor)) {
            if (!hidden.has(cursor))
                return cursor;
            guard.add(cursor);
            cursor = parentOf.get(cursor);
        }
        return null;
    };
    const nodes = layout.nodes.filter((n) => !hidden.has(n.id));
    // Port-name set per node, for port-AWARE re-anchoring (black-box ports). A
    // collapsed container keeps rendering its own boundary ports (PartNode's leaf
    // body draws `data.portsIn`/`portsOut`), so when a boundary flow re-anchors
    // onto that container we PRESERVE the port handle IF the container actually
    // declares that port — the flow then connects to the black box's PORT instead
    // of its centre. When the handle names a port the container does NOT own (a
    // direct child→external edge whose child port has no boundary equivalent), we
    // drop the handle and fall back to centre anchoring.
    const portCache = new Map();
    const nodeHasPort = (nodeId, handle) => {
        if (handle === undefined)
            return false;
        const portName = handle.replace(/\.(s|t)$/, ''); // handles are `${port}.s`/`.t`
        let ports = portCache.get(nodeId);
        if (ports === undefined) {
            const node = layout.nodes.find((n) => n.id === nodeId);
            const data = (node?.data ?? {});
            ports = new Set();
            for (const p of [...(data.portsIn ?? []), ...(data.portsOut ?? [])]) {
                if (typeof p.name === 'string' && p.name.length > 0)
                    ports.add(p.name);
            }
            portCache.set(nodeId, ports);
        }
        return ports.has(portName);
    };
    const seen = new Set();
    const edges = [];
    for (const edge of layout.edges) {
        const s = visibleAnchor(edge.source);
        const t = visibleAnchor(edge.target);
        if (s === null || t === null)
            continue; // no visible endpoint → drop
        if (s === t)
            continue; // internal-to-a-collapsed-container → drop
        const sReanchored = s !== edge.source;
        const tReanchored = t !== edge.target;
        const reanchored = sReanchored || tReanchored;
        // Preserve each side's port handle when the (possibly re-anchored) endpoint
        // OWNS that port — so a boundary flow connects to the black box's PORT, not
        // its centre. A handle naming a port the container doesn't own is dropped.
        const sourceHandle = !sReanchored
            ? edge.sourceHandle
            : nodeHasPort(s, edge.sourceHandle)
                ? edge.sourceHandle
                : undefined;
        const targetHandle = !tReanchored
            ? edge.targetHandle
            : nodeHasPort(t, edge.targetHandle)
                ? edge.targetHandle
                : undefined;
        // De-dup boundary edges collapsing onto the SAME endpoints + handles.
        const key = `${s}|${sourceHandle ?? ''}|${t}|${targetHandle ?? ''}`;
        if (reanchored && seen.has(key))
            continue;
        seen.add(key);
        if (!reanchored) {
            edges.push(edge);
        }
        else {
            const next = { id: edge.id, source: s, target: t };
            if (edge.type !== undefined)
                next.type = edge.type;
            if (sourceHandle !== undefined)
                next.sourceHandle = sourceHandle;
            if (targetHandle !== undefined)
                next.targetHandle = targetHandle;
            if (edge.data !== undefined)
                next.data = edge.data;
            edges.push(next);
        }
    }
    return { ...layout, nodes, edges };
}
//# sourceMappingURL=view-scope.js.map